import LoadingErrorWrapper from '../shared_components/loadingErrorWrapper';
import React, { useState, useEffect, Suspense, useMemo, useCallback, useRef } from 'react';
import { 
  Skeleton, 
  Box,
  CircularProgress,
  Typography
} from '@mui/material';
import {
  MapsHomeWork,
  AutoGraphOutlined,
  PersonPinCircleOutlined,
  Explore,
  PeopleOutlined,
  GroupsOutlined,
  CompareArrowsOutlined
} from '@mui/icons-material';

// Shared Components
import PageLayout from '../shared_components/pageLayout';
import AccordionSection from '../shared_components/accordionSection';
import AccordionContainer from '../shared_components/accordionContainer';
import StickyFilters from '../shared_components/stickyFilters';
import InitialSchoolSelector from '../shared_components/initialSchoolSelector';
import NowWhatSection from '../shared_components/templates/nowWhatTemplate';
import CompXNearbyMapPopup from './nearby/CompXNearbyMapPopup';
import AISummaryAccordion from '../shared_components/AISummaryAccordion';

// Custom Hook
import { useAccordionState } from '../../hooks/useAccordionState';
import { useFeatureUsage, PRODUCT_AREAS } from '../../hooks/useFeatureUsage';

// Contexts
import { useInsight } from '../../contexts/InsightContext';

// Utils
import { filterSchools, getAvailableSchoolTypes, getSchoolGrades } from '../../utils/schoolUtils';
import { formatSchoolName } from '../../utils/nameFormatter';
import { detectSchoolDataType } from '../../utils/schoolDataDetection';

// Stores
import useSchoolStore from '../../stores/schoolStore';

// Cache
import { useTeamSchoolCache } from '../../hooks/teamCacheService';

// Components
import CompXCommunityHeadlines from './community/CompXCommunityHeadlines';

// API
import { schoolsApi } from '../../utils/apiService'; 

// Lazy load components
const CompXNearbyHeadlines = React.lazy(() => import('./nearby/CompXNearbyHeadlines'));
const CompXEnrollmentHeadlines = React.lazy(() => import('./enrollment/CompXEnrollmentHeadlines'));

const SectionLoadingPlaceholder = ({ message }) => (
  <Box 
    sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      py: 8 
    }}
  >
    <CircularProgress size={40} sx={{ mb: 2 }} />
    <Typography variant="body1" color="text.secondary">
      {message}
    </Typography>
  </Box>
);

function CompetitiveExplorer() {
  const trackUsage = useFeatureUsage(PRODUCT_AREAS.COMPETITIVE_EXPLORER);

  // Get data from team school cache
  const { 
    schools, 
    loading: schoolsLoading, 
  } = useTeamSchoolCache();

  // Use school store directly
  const {
    selectedSchool,
    esriData,
    nearbySchools,
    schoolsEnrollmentData,
    loadingStates,
    districtBoundaries,
    error,
    availableTravelModes,
    selectedTravelMode,
    changeTravelMode,
  } = useSchoolStore();

  // Context declarations
  const { setToolAndSection } = useInsight();
  
  // Refs
  const sectionRefs = useRef({});
  const observerRef = useRef(null);
  const prevNearbyProps = useRef({});
  const initialSchoolLoadRef = useRef(false);
  const initialDropAPinGradesSet = useRef(false);

  // State declarations
  const [governanceFilter, setGovernanceFilter] = useState(['Charter', 'District']);
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [selectedDriveTime, setSelectedDriveTime] = useState(10);
  const [schoolTypeFilter, setSchoolTypeFilter] = useState([]);
  const [availableSchoolTypes, setAvailableSchoolTypes] = useState([]);

  // Derive which drive times actually have data for the selected school
  const availableDriveTimes = useMemo(() => {
    if (!esriData?.drive_times) return null;
    const times = Object.keys(esriData.drive_times).map(Number).sort((a, b) => a - b);
    return times.length > 0 ? times : null;
  }, [esriData]);

  // Auto-reset drive time when the available options change and current selection isn't in the list
  useEffect(() => {
    if (availableDriveTimes && !availableDriveTimes.includes(selectedDriveTime)) {
      const fallback = availableDriveTimes.includes(10) ? 10 : availableDriveTimes[0];
      setSelectedDriveTime(fallback);
    }
  }, [availableDriveTimes]); // eslint-disable-line react-hooks/exhaustive-deps
  const [sectionLoadingStates, setSectionLoadingStates] = useState({
    communityHeadlines: { loading: true, dataReady: false },
    enrollmentTrends: { loading: true, dataReady: false },
    nearby: { loading: true, dataReady: false },
    nowWhat: { loading: false, dataReady: true }
  });

  // Detect school data type for conditional section display
  const schoolDataDetection = useMemo(() => {
    if (!selectedSchool) {
      return {
        hasEnrollmentData: false,
        hasRaceData: false,
        isDropAPinLocation: false,
        dataType: 'unknown'
      };
    }

    const detection = detectSchoolDataType(selectedSchool, schoolsEnrollmentData);
    
    // Check if the school has race data (demographic data beyond just enrollment totals)
    const enrollmentRecord = schoolsEnrollmentData?.find(d => d.uuid === selectedSchool.uuid);
    let hasActualRaceData = false;
    
    if (enrollmentRecord?.race_data?.current) {
      // Check if any grade has non-zero race data
      hasActualRaceData = Object.values(enrollmentRecord.race_data.current).some(gradeData => {
        if (!gradeData || typeof gradeData !== 'object') return false;
        return Object.values(gradeData).some(count => (count || 0) > 0);
      });
    }

    return {
      ...detection,
      hasRaceData: hasActualRaceData,
      enrollmentRecord
    };
  }, [selectedSchool, schoolsEnrollmentData]);

  // Section data loading check
  const isSectionDataLoaded = useCallback((sectionId) => {
    if (!loadingStates || typeof loadingStates !== 'object') {
      return false;
    }

    const { isDropAPinLocation } = schoolDataDetection;

    switch (sectionId) {
      case 'communityHeadlines':
        // Show for all types if we have ESRI data, but may have different content
        return !!esriData && 
               !!selectedSchool && 
               !!esriData.drive_times && 
               Object.keys(esriData.drive_times).length > 0 && 
               loadingStates.marketShare === false;
      
      case 'enrollmentTrends': 
        // For drop-a-pin locations, this section shows nearby schools analysis only
        if (isDropAPinLocation) {
          return !!nearbySchools &&
                 Object.keys(nearbySchools?.current || {}).length > 0 &&
                 loadingStates.nearby === false;
        }
        
        // For schools with enrollment data, check standard requirements
        return !!schoolsEnrollmentData.length && 
               !!selectedSchool && 
               !!esriData && 
               !!selectedSchool.grade_range && 
               !!nearbySchools &&
               loadingStates.enrollment === false && 
               loadingStates.nearby === false;

      case 'nearby':
        return !!nearbySchools && 
               Object.keys(nearbySchools?.current || {}).length > 0 &&
               !!schoolsEnrollmentData.length && 
               !!esriData && 
               !!selectedSchool &&
               loadingStates.nearby === false && 
               loadingStates.enrollment === false;

      default: 
        return false;
    }
  }, [esriData, schoolsEnrollmentData.length, nearbySchools, selectedSchool, loadingStates, schoolDataDetection]);

  // Accordion state
  const {
    expandedSections,
    handleExpandedChange,
    handleExpandAll,
    handleCollapseAll,
  } = useAccordionState({
    communityHeadlines: false,
    enrollmentTrends: false,
    nearby: false,
    nowWhat: false
  });

  // Event handlers
  const handleSchoolChange = useCallback((event) => {
    const schoolId = event.target.value;
    const school = schools.find(s => s.uuid === schoolId);
    if (school) {
      useSchoolStore.getState().selectSchool({
        ...school,
        name: formatSchoolName(school.name)
      });
    }
  }, [schools]);

  const handleGradeChange = useCallback((event) => {
    const value = event.target.value;
    setSelectedGrades(typeof value === 'string' ? value.split(',') : value);
  }, []);

  const handleGovernanceChange = useCallback((event) => {
    const value = event.target.value;
    const newValue = typeof value === 'string' ? value.split(',') : value;
    // Require at least one governance type to be selected
    if (newValue.length === 0) {
      return;
    }
    setGovernanceFilter(newValue);
  }, []);

  const handleDriveTimeChange = useCallback((event) => {
    setSelectedDriveTime(Number(event.target.value));
  }, []);

  const handleTravelModeChange = useCallback((event) => {
    changeTravelMode(event.target.value);
  }, [changeTravelMode]);

  const handleSchoolTypeChange = useCallback((event) => {
    const value = event.target.value;
    const newValue = typeof value === 'string' ? value.split(',') : value;
    // Require at least one school type to be selected
    if (newValue.length === 0) {
      return;
    }
    setSchoolTypeFilter(newValue);
  }, []);

  const handleSectionChange = useCallback((sectionId) => (event, isExpanded) => {
    if (isExpanded && !sectionLoadingStates[sectionId]?.dataReady) {
      event.preventDefault();
      return false;
    }
    
    handleExpandedChange(sectionId)(event, isExpanded);
    if (isExpanded) {
      setToolAndSection('competitive-explorer', sectionId); 
    } else if (!Object.values(expandedSections).some(expanded => expanded)) {
      setToolAndSection('competitive-explorer', null); 
    }
  }, [handleExpandedChange, expandedSections, setToolAndSection, sectionLoadingStates]);

  const getSectionRef = useCallback((sectionId) => (element) => {
    if (element) {
      sectionRefs.current[sectionId] = element;
      if (observerRef.current) {
        observerRef.current.observe(element);
      }
    }
  }, []);

  // Filtered schools computation
  const filteredSchools = useMemo(() => {
    if (!selectedSchool || !nearbySchools) {
      return [];
    }
  
    if (!selectedGrades || selectedGrades.length === 0) {
      return [];
    }
    
    const { hasEnrollmentData } = detectSchoolDataType(selectedSchool, schoolsEnrollmentData);
    const schoolsForSelectedDriveTime = nearbySchools?.current?.[selectedDriveTime] || [];
    
    // For locations without enrollment data, exclude from filtered schools list
    // but still show nearby schools for analysis
    const allSchools = hasEnrollmentData 
      ? [
          { 
            ...selectedSchool, 
            drive_time: 0 
          }, 
          ...schoolsForSelectedDriveTime.map(school => ({
            ...school,
            drive_time: selectedDriveTime
          }))
        ]
      : schoolsForSelectedDriveTime.map(school => ({
          ...school,
          drive_time: selectedDriveTime
        }));

    return filterSchools(
      allSchools,
      {
        selectedGrades,
        governanceFilter,
        schoolTypeFilter
      },
      schoolsEnrollmentData
    );
  }, [
    selectedSchool,
    nearbySchools,
    selectedDriveTime,
    selectedGrades,
    governanceFilter,
    schoolTypeFilter,
    schoolsEnrollmentData
  ]);

  // Generate section configurations based on school data type
  const sectionConfigs = useMemo(() => {
    const { isDropAPinLocation } = schoolDataDetection;
    const schoolName = selectedSchool?.name || 'the selected school';
    
    const configs = {
      communityHeadlines: {
        show: !isDropAPinLocation, // Hide for drop-a-pin locations
        title: "Your Community",
        summary: `Share of estimated school-age children population within ${selectedDriveTime} minutes of ${schoolName}`,
        icon: PeopleOutlined
      },
      enrollmentTrends: {
        show: !isDropAPinLocation, // Hide completely for drop-a-pin locations since they're mostly zeros
        title: "Enrollment & Demographic Trends",
        summary: `Share of students enrolled in public schools within ${selectedDriveTime} minutes of ${schoolName}`,
        icon: GroupsOutlined
      },
      nearby: {
        show: true, // Always show
        title: "Nearby Schools Comparison",
        summary: isDropAPinLocation
          ? `Area schools within ${selectedDriveTime} minutes of this location`
          : `Area school market share and changes in market share within ${selectedDriveTime} minutes of ${schoolName}`,
        icon: CompareArrowsOutlined
      }
    };

    return configs;
  }, [schoolDataDetection, selectedDriveTime, selectedSchool?.name]);

  // Props for nearby headlines
  const nearbyHeadlinesProps = useMemo(() => ({
    selectedSchool,
    filteredSchools: filteredSchools || [],
    schoolsEnrollmentData: schoolsEnrollmentData || [],
    selectedGrades: selectedGrades || [],
    governanceFilter,
    schoolTypeFilter,
    nearbySchools: nearbySchools || {},
    selectedDriveTime,
    selectedTravelMode,
    esriData: esriData || {},
    visibleDriveTimes: [selectedDriveTime],
    schoolDataDetection,  // Pass detection results to components
    districtBoundaries
  }), [
    selectedSchool,
    filteredSchools,
    schoolsEnrollmentData,
    selectedGrades,
    governanceFilter,
    schoolTypeFilter,
    nearbySchools,
    selectedDriveTime,
    selectedTravelMode,
    districtBoundaries,
    esriData,
    schoolDataDetection
  ]);

  // Effects
  useEffect(() => {
    if (schools.length > 0 && !selectedSchool && !initialSchoolLoadRef.current) {
      const params = new URLSearchParams(window.location.search);
      const schoolId = params.get('school');
      
      if (schoolId) {
        const school = schools.find(s => s.uuid === schoolId);
        if (school) {
          useSchoolStore.getState().selectSchool({
            ...school,
            name: formatSchoolName(school.name)
          });
        }
        initialSchoolLoadRef.current = true;
      }
    }
  }, [schools, selectedSchool]);

  // Watch for URL changes even after a school is already selected
  // This ensures navigation from other tools (like AI Summary) updates the selected school
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const schoolIdFromUrl = params.get('school');
    
    // If URL has a school parameter and it's different from the currently selected school
    if (schoolIdFromUrl && schools.length > 0 && selectedSchool?.uuid !== schoolIdFromUrl) {
      const school = schools.find(s => s.uuid === schoolIdFromUrl);
      if (school) {
        useSchoolStore.getState().selectSchool({
          ...school,
          name: formatSchoolName(school.name)
        });
      }
    }
  }, [window.location.search, schools, selectedSchool?.uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-set grades when a school is selected
  useEffect(() => {
    if (selectedSchool && selectedSchool.grade_range && selectedGrades.length === 0) {
      // Parse the grade range to extract individual grades
      const parseGradeRange = (gradeRange) => {
        if (!gradeRange) return [];
        
        // Handle new shorthand format: "PK-08", "KG-05", "06-08", etc.
        const shorthandMatch = gradeRange.match(/^(PK|KG|0[1-9]|1[0-2])-(0[1-9]|1[0-2])$/);
        if (shorthandMatch) {
          const start = shorthandMatch[1];
          const end = shorthandMatch[2];
          const grades = [];
          
          // Start grade
          if (start === 'PK') {
            grades.push('Pre-Kindergarten');
            grades.push('Kindergarten');
            for (let i = 1; i <= parseInt(end); i++) {
              grades.push(i.toString());
            }
          } else if (start === 'KG') {
            grades.push('Kindergarten');
            for (let i = 1; i <= parseInt(end); i++) {
              grades.push(i.toString());
            }
          } else {
            // Numeric start (e.g., "06-08")
            const startNum = parseInt(start);
            const endNum = parseInt(end);
            for (let i = startNum; i <= endNum; i++) {
              grades.push(i.toString());
            }
          }
          
          return grades;
        }
        
        // Handle single grade shorthand: "PK", "KG", "01", "02", etc.
        if (gradeRange === 'PK') return ['Pre-Kindergarten'];
        if (gradeRange === 'KG') return ['Kindergarten'];
        const singleGradeMatch = gradeRange.match(/^(0[1-9]|1[0-2])$/);
        if (singleGradeMatch) {
          return [parseInt(singleGradeMatch[1]).toString()];
        }
        
        // Handle "Grade X - Grade Y" format (legacy)
        const gradeRangeMatch = gradeRange.match(/Grade (\d+) - Grade (\d+)/);
        if (gradeRangeMatch) {
          const startGrade = parseInt(gradeRangeMatch[1]);
          const endGrade = parseInt(gradeRangeMatch[2]);
          const grades = [];
          for (let i = startGrade; i <= endGrade; i++) {
            grades.push(i.toString());
          }
          return grades;
        }
        
        // Handle single "Grade X" format (legacy)
        const singleLegacyMatch = gradeRange.match(/Grade (\d+)/);
        if (singleLegacyMatch) {
          return [singleLegacyMatch[1]];
        }
        
        // Handle "Kindergarten" variations
        if (gradeRange.includes('Kindergarten')) {
          return ['Kindergarten'];
        }
        
        // Default fallback - return all middle school grades for analysis
        return ['6', '7', '8'];
      };
      
      const grades = parseGradeRange(selectedSchool.grade_range);
      setSelectedGrades(grades);
    }
  }, [selectedSchool, selectedGrades.length]);

  // Auto-set grades for drop-a-pin locations (one-time init, like regular schools)
  useEffect(() => {
    if (!selectedSchool) {
      initialDropAPinGradesSet.current = false;
      return;
    }
    if (initialDropAPinGradesSet.current) return;

    const { isDropAPinLocation } = detectSchoolDataType(selectedSchool, schoolsEnrollmentData);

    if (isDropAPinLocation) {
      if (selectedSchool.grade_range) {
        const schoolGrades = getSchoolGrades(selectedSchool.grade_range);
        setSelectedGrades(schoolGrades);
      } else if (selectedGrades.length === 0) {
        const allGrades = ['Kindergarten', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
        setSelectedGrades(allGrades);
      }
      initialDropAPinGradesSet.current = true;
    }
  // selectedGrades is intentionally NOT in the dependency array to avoid overriding user changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchool, schoolsEnrollmentData]);

  useEffect(() => {
    if (selectedSchool) {
      setSectionLoadingStates({
        communityHeadlines: { loading: true, dataReady: false },
        enrollmentTrends: { loading: true, dataReady: false }, 
        nearby: { loading: true, dataReady: false },
        nowWhat: { loading: false, dataReady: true }
      });
      
      Object.keys(expandedSections).forEach(section => {
        if (expandedSections[section]) {
          handleExpandedChange(section)(null, false);
        }
      });
  
      if (selectedSchool.uuid) {
        useSchoolStore.getState().markDataStale();
      }
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchool?.uuid, handleExpandedChange]);

  useEffect(() => {
    if (selectedSchool?.uuid) {
      trackUsage('school_analyzed', { schoolUuid: selectedSchool.uuid });
    }
  }, [selectedSchool?.uuid, trackUsage]);

  useEffect(() => {
    if (selectedSchool) {
      const controller = new AbortController();
      const signal = controller.signal;
      
      setSectionLoadingStates(prev => ({
        ...prev,
        communityHeadlines: { loading: !isSectionDataLoaded('communityHeadlines'), dataReady: isSectionDataLoaded('communityHeadlines') },
        enrollmentTrends: { loading: !isSectionDataLoaded('enrollmentTrends'), dataReady: isSectionDataLoaded('enrollmentTrends') },
        nearby: { loading: !isSectionDataLoaded('nearby'), dataReady: isSectionDataLoaded('nearby') }
      }));
      
      const loadData = async () => {
        try {
          if (!isSectionDataLoaded('communityHeadlines')) {
            await schoolsApi.getMarketShareData(selectedSchool.uuid, { signal });
            setSectionLoadingStates(prev => ({
              ...prev,
              communityHeadlines: { loading: false, dataReady: true }
            }));
          }
          
          let nearbyData;
          if (!isSectionDataLoaded('nearby') || !isSectionDataLoaded('enrollmentTrends')) {
            nearbyData = await schoolsApi.getAllNearbySchools(selectedSchool.uuid);
          }
          
          if (!isSectionDataLoaded('enrollmentTrends')) {
            await schoolsApi.getSchoolsEnrollmentData(
              [selectedSchool.uuid],
              selectedSchool.uuid,
              { signal }
            );
            
            setSectionLoadingStates(prev => ({
              ...prev,
              enrollmentTrends: { loading: false, dataReady: true }
            }));
          }
          
          if (!isSectionDataLoaded('nearby') && nearbyData && !signal.aborted) {
            const schoolIds = new Set([
              selectedSchool.uuid,
              ...Object.values(nearbyData.current || {})
                .flatMap(schools => schools.map(s => s.uuid))
            ]);
            
            await schoolsApi.getSchoolsEnrollmentData(
              Array.from(schoolIds),
              selectedSchool.uuid,
              { signal }
            );
            
            setSectionLoadingStates(prev => ({
              ...prev,
              nearby: { loading: false, dataReady: true }
            }));
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('Error loading section data:', error);
          }
        }
      };
      
      loadData();
      
      return () => {
        controller.abort();
      };
    }
  }, [selectedSchool, isSectionDataLoaded]);

  useEffect(() => {
    setToolAndSection('competitive-explorer', null);
    
    return () => {
      const geoExplorerExists = document.querySelector('[data-tool="geographic-explorer"]');
      const compExplorerExists = document.querySelector('[data-tool="competitive-explorer"]');
      
      if (!geoExplorerExists && !compExplorerExists) {
        setToolAndSection(null, null);
      }
    };
  }, [setToolAndSection]);

  useEffect(() => {
    observerRef.current = new IntersectionObserver((entries) => {
      let maxVisibility = 0;
      let mostVisible = null;
  
      entries.forEach(entry => {
        const sectionId = entry.target.getAttribute('data-section-id');
        if (entry.intersectionRatio > maxVisibility) {
          maxVisibility = entry.intersectionRatio;
          mostVisible = sectionId;
        }
      });
  
      if (mostVisible && maxVisibility > 0.5) {
        setToolAndSection('competitive-explorer', mostVisible);
      }
    }, {
      threshold: [0, 0.5, 1],
      rootMargin: '0px 0px -10% 0px'
    });
  
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [setToolAndSection]);

  useEffect(() => {
    if (schools.length === 0) {
      useSchoolStore.getState().fetchSchools();
    }
  }, [schools.length]);

  useEffect(() => {
    if (schoolsEnrollmentData.length > 0) {
      const types = getAvailableSchoolTypes(schoolsEnrollmentData);
      const typesChanged = JSON.stringify(types.sort()) !== JSON.stringify(availableSchoolTypes.sort());
      
      if (typesChanged) {
        setAvailableSchoolTypes(types);
        // When available types change, update filter to include all new types
        if (types.length > 0) {
          setSchoolTypeFilter(types);
        }
      }
    }
  }, [schoolsEnrollmentData, availableSchoolTypes]);

  useEffect(() => {
    const hasExpandedSection = Object.values(expandedSections).some(isExpanded => isExpanded);
    if (!hasExpandedSection) {
      setToolAndSection('competitive-explorer', null);
    }
  }, [expandedSections, setToolAndSection]);

  useEffect(() => {
    const changedProps = {};
    const currentProps = {
      selectedSchool,
      filteredSchools,
      schoolsEnrollmentData,
      selectedGrades,
      selectedDriveTime,
      governanceFilter,
      schoolTypeFilter,
      nearbySchools,
      esriData
    };

    Object.entries(currentProps).forEach(([key, value]) => {
      if (prevNearbyProps.current[key] !== value) {
        changedProps[key] = key;
      }
    });

    prevNearbyProps.current = currentProps;
  }, [
    selectedSchool,
    filteredSchools,
    schoolsEnrollmentData,
    selectedGrades,
    selectedDriveTime,
    governanceFilter,
    schoolTypeFilter,
    nearbySchools,
    esriData
  ]);

  // Define actions for Now What section
  const actions = [
    {
      icon: MapsHomeWork,
      title: 'Geographic Explorer',
      description: 'Explore demographic and enrollment trends in your community',
      path: `/geographic-explorer${selectedSchool ? `?school=${selectedSchool.uuid}` : ''}`
    },
    {
      icon: AutoGraphOutlined,
      title: 'Projections Explorer',
      description: 'Explore historical and projected enrollment data for the next five years',
      path: `/projections-explorer${selectedSchool ? `?school=${selectedSchool.uuid}` : ''}`
    },
    {
      icon: PersonPinCircleOutlined,
      title: 'Student Catchment Explorer',
      description: 'Connect your student locations to community and competitive insights',
      path: `/student-catchment-explorer${selectedSchool ? `?school=${selectedSchool.uuid}` : ''}`
    }
  ];

  return (
    <PageLayout
      data-tool="competitive-explorer"
      title="Competitive Explorer"
      description={selectedSchool
        ? "Analyze market share data for the selected school. Use the filters to refine your analysis."
        : "Use this tool to analyze market share by students who reside and attend schools in your area. Start by selecting a school."
      }
    >
      <LoadingErrorWrapper
        loading={schoolsLoading}
        error={error}
        initialLoading={!schools || !schools.length}
        hideSteps={true}
      >
        {!selectedSchool ? (
          <InitialSchoolSelector
            schools={schools}
            selectedSchool={selectedSchool}
            onSchoolChange={handleSchoolChange}
          />
        ) : (
          <React.Fragment>
            <Box sx={{ position: 'relative' }}> 
              <StickyFilters
                schools={schools}
                selectedSchool={selectedSchool}
                governanceFilter={governanceFilter}
                selectedGrades={selectedGrades}
                selectedDriveTime={selectedDriveTime}
                selectedTravelMode={selectedTravelMode}
                schoolTypeFilter={schoolTypeFilter}
                availableSchoolTypes={availableSchoolTypes}
                availableDriveTimes={availableDriveTimes}
                availableTravelModes={availableTravelModes}
                onSchoolChange={handleSchoolChange}
                onGovernanceChange={handleGovernanceChange}
                onGradeChange={handleGradeChange}
                onDriveTimeChange={handleDriveTimeChange}
                onTravelModeChange={handleTravelModeChange}
                onSchoolTypeChange={handleSchoolTypeChange}
                expandedSections={expandedSections}
                onExpandAll={handleExpandAll}
                onCollapseAll={handleCollapseAll}
                showSchoolType={true}
              />
    
              {/* AI Summary Accordion - Executive Summary */}
              <AISummaryAccordion
                selectedSchool={selectedSchool}
                driveTime={selectedDriveTime}
                summaryType="competitive"
                travelMode={selectedTravelMode}
                defaultExpanded={true}
              />

              <AccordionContainer 
                expandedSections={expandedSections}
                onSectionChange={handleSectionChange}
              >
                {/* Community Section - Hidden for drop-a-pin locations */}
                {sectionConfigs.communityHeadlines.show && (
                  <AccordionSection
                    id="communityHeadlines"
                    title={sectionConfigs.communityHeadlines.title}
                    summary={sectionConfigs.communityHeadlines.summary}
                    icon={sectionConfigs.communityHeadlines.icon}
                    disabled={!sectionLoadingStates.communityHeadlines.dataReady}
                    loading={sectionLoadingStates.communityHeadlines.loading}
                  >
                    <div 
                      ref={getSectionRef("communityHeadlines")} 
                      data-section-id="communityHeadlines"
                    >
                      <Suspense fallback={<Skeleton variant="rectangular" height={200} />}>
                      {sectionLoadingStates.communityHeadlines.dataReady ? (
                        <CompXCommunityHeadlines
                          selectedSchool={selectedSchool}
                          filteredSchools={filteredSchools || []}
                          selectedDriveTime={selectedDriveTime}
                          selectedTravelMode={selectedTravelMode}
                          schoolsEnrollmentData={schoolsEnrollmentData || []}
                          selectedGrades={selectedGrades || []}
                          esriData={esriData || {}}
                          nearbySchools={nearbySchools || {}}
                          schoolDataDetection={schoolDataDetection}
                        />
                      ) : (
                        <SectionLoadingPlaceholder 
                          message="Loading community data..." 
                        />
                      )}
                      </Suspense>
                    </div>
                  </AccordionSection>
                )}
    
                {/* Enrollment Trends Section - Always shown but content varies */}
                {sectionConfigs.enrollmentTrends.show && (
                  <AccordionSection
                    id="enrollmentTrends"
                    title={sectionConfigs.enrollmentTrends.title}
                    summary={sectionConfigs.enrollmentTrends.summary}
                    icon={sectionConfigs.enrollmentTrends.icon}
                    disabled={!sectionLoadingStates.enrollmentTrends.dataReady}
                    loading={sectionLoadingStates.enrollmentTrends.loading}
                  >
                    <div 
                      ref={getSectionRef("enrollmentTrends")} 
                      data-section-id="enrollmentTrends"
                    >
                      <Suspense fallback={<Skeleton variant="rectangular" height={200} />}>
                        {sectionLoadingStates.enrollmentTrends.dataReady ? (
                          <CompXEnrollmentHeadlines
                            selectedSchool={selectedSchool}
                            filteredSchools={filteredSchools || []}
                            selectedGrades={selectedGrades || []}
                            schoolsEnrollmentData={schoolsEnrollmentData || []}
                            esriData={esriData || {}}
                            selectedDriveTime={selectedDriveTime}
                            nearbySchools={nearbySchools || {}} 
                            governanceFilter={governanceFilter}
                            schoolDataDetection={schoolDataDetection}
                          />
                        ) : (
                          <SectionLoadingPlaceholder 
                            message="Loading enrollment data..." 
                          />
                        )}
                      </Suspense>
                    </div>
                  </AccordionSection>
                )}
    
                {/* Nearby Schools Section - Always shown but content varies */}
                {sectionConfigs.nearby.show && (
                  <AccordionSection
                    id="nearby"
                    title={sectionConfigs.nearby.title}
                    summary={sectionConfigs.nearby.summary}
                    icon={sectionConfigs.nearby.icon}
                    disabled={!sectionLoadingStates.nearby.dataReady}
                    loading={sectionLoadingStates.nearby.loading}
                  >
                    <div 
                      ref={getSectionRef("nearby")} 
                      data-section-id="nearby"
                    >
                      <Suspense fallback={<Skeleton variant="rectangular" height={500} />}>
                        {sectionLoadingStates.nearby.dataReady ? (
                          <CompXNearbyHeadlines
                            {...nearbyHeadlinesProps}
                            PopupComponent={CompXNearbyMapPopup}
                          />
                        ) : (
                          <SectionLoadingPlaceholder 
                            message="Loading comparison data..." 
                          />
                        )}
                      </Suspense>
                    </div>
                  </AccordionSection>
                )}
    
                <AccordionSection
                  id="nowWhat"
                  title="Now What?"
                  summary="Continue your analysis with these related tools"
                  icon={Explore}
                  disabled={false}
                  loading={false}
                >
                  <div 
                    ref={getSectionRef("nowWhat")} 
                    data-section-id="nowWhat"
                  >
                    <Suspense fallback={<Skeleton variant="rectangular" height={200} />}>
                      <NowWhatSection 
                        sectionIcon={Explore}
                        actions={actions}
                      />
                    </Suspense>
                  </div>
                </AccordionSection>
              </AccordionContainer>
            </Box>
          </React.Fragment>
        )}
      </LoadingErrorWrapper>
    </PageLayout>
  );
}

export default CompetitiveExplorer;